'use client';

import React from 'react';
import styles from './Loader.module.scss';
import Row from '../Grid/Row/Row';
import Col from '../Grid/Col/Col';
import Iconography from '../Iconography/Iconography';

export type LoaderSize = 16 | 24 | 32 | 48 | 64 | 90;

export interface LoaderProps {
  /** Size of the loader in pixels */
  size?: LoaderSize;
  /** Additional CSS class name */
  className?: string;
  /** Theme variant for the loader */
  theme?: 'dark' | 'light';
}

/**
 * Stroke widths per size (in rendered px).
 * viewBox stroke = (px / size) * 124
 */
const STROKE_BY_SIZE: Record<LoaderSize, number> = {
  16: 1.5,
  24: 2.4,
  32: 3.2,
  48: 4.8,
  64: 6,
  90: 8,
};

const BEAR_ICON_SIZES: Record<LoaderSize, number> = {
  16: 0,
  24: 16,
  32: 24,
  48: 32,
  64: 32,
  90: 64,
};

/** Gradient configs per theme matching Figma specs */
const THEME_CONFIG = {
  dark: {
    gradientStart: { color: '#D7BE96', opacity: 1 },
    gradientMid: { color: '#D7BE96', opacity: 0.5 },
    gradientEnd: { color: '#000000', opacity: 0 },
    ringColor: '#D7BE96',
    ringOpacity: 0.2,
    bearColor: 'primary-1' as const,
  },
  light: {
    gradientStart: { color: '#000000', opacity: 1 },
    gradientMid: { color: '#000000', opacity: 0.5 },
    gradientEnd: { color: '#AD6B08', opacity: 0 },
    ringColor: '#B2B2B2',
    ringOpacity: 0.4,
    bearColor: 'black' as const,
  },
} as const;

/**
 * Loader component that displays a spinning ring with the Lucid bear icon.
 *
 * @component
 * @param {LoaderProps} props - Component props
 * @returns {React.ReactElement} Loader element
 * @example
 * <Loader size={48} theme="dark" />
 */
const Loader: React.FC<LoaderProps> = ({ size = 48, className, theme = 'dark' }) => {
  const bearSize = BEAR_ICON_SIZES[size];
  const strokePx = STROKE_BY_SIZE[size];
  const viewBoxStroke = (strokePx / size) * 124;

  const config = THEME_CONFIG[theme];
  const gradientId = `loader-gradient-${theme}`;
  const showBear = bearSize > 0;

  return (
    <Row justify="center" className={className}>
      <Col xs={12}>
        <div className={styles.loaderWrapper}>
          <svg
            className={styles.loader}
            height={size}
            width={size}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 124 124"
            role="img"
            aria-label="Loading"
          >
            <g className={styles.spinner}>
              <circle
                cx="62"
                cy="62"
                r="60"
                stroke={config.ringColor}
                strokeOpacity={config.ringOpacity}
                strokeWidth={viewBoxStroke}
              />
              <defs>
                <linearGradient id={gradientId} x1="100%" y1="0%" x2="0%" y2="100%">
                  <stop
                    offset="0%"
                    stopColor={config.gradientEnd.color}
                    stopOpacity={config.gradientEnd.opacity}
                  />
                  <stop
                    offset="40%"
                    stopColor={config.gradientMid.color}
                    stopOpacity={config.gradientMid.opacity}
                  />
                  <stop
                    offset="100%"
                    stopColor={config.gradientStart.color}
                    stopOpacity={config.gradientStart.opacity}
                  />
                </linearGradient>
              </defs>
              <path
                stroke={`url(#${gradientId})`}
                strokeWidth={viewBoxStroke}
                d="M 62 2 A 60 60 0 1 1 2 62"
              />
            </g>
          </svg>
          {showBear && (
            <div className={styles.bearIcon}>
              <Iconography
                category="brand"
                name="bear"
                size={bearSize as 16 | 24 | 32 | 64}
                color={config.bearColor}
                dataTestId="loader-bear-icon"
              />
            </div>
          )}
        </div>
      </Col>
    </Row>
  );
};

Loader.displayName = 'Loader';

export default Loader;
